import { useEffect, useMemo, useState } from "react"
import { Shell } from "@/components/Shell"
import { useAuth } from "@/lib/auth"
import {
  getMealHistory,
  type DayTotals,
  type MealHistory,
  type Targets,
} from "@/lib/api"
import { formatDateRelative, localToday } from "@/lib/dates"

/**
 * Performance — a 30-day ledger of how each day measured up to the user's
 * macro targets.
 *
 * Editorial intent: this is the "back of the magazine" — a quiet,
 * numerically dense column that rewards scanning. Hero stats up top,
 * then a long thin spine of days, each with four at-a-glance status dots
 * (cal · protein · fiber · sugar) and a one-line numeric summary.
 *
 * Why four dots, not five: carbs is informational (we display the running
 * total but don't grade it). The graded macros are the ones with a hard
 * "good"/"not" reading — under-budget on calories+sugar, at-or-above on
 * protein+fiber.
 *
 * "Ideal day" definition (drives streaks + hero count):
 *   calories ∈ [50%, 100%] of target  (∗)
 *   protein  ≥ target
 *   fiber    ≥ target
 *   sugar    ≤ target
 *
 *   (∗) The lower bound rules out near-zero days. A day logged at 200 kcal
 *       isn't an "ideal day," it's a partially-logged day. 50% is a
 *       defensible floor without being preachy about it.
 */

const DAYS_BACK = 30

export function PerformancePage() {
  const auth = useAuth()
  const [history, setHistory] = useState<MealHistory | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        setHistory(await getMealHistory(DAYS_BACK))
      } catch (e: any) {
        setErr(e?.message ?? "Load failed")
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  if (auth.status !== "authed") return null
  const targets = auth.me.targets

  return (
    <Shell
      currentPath="/performance"
      navItems={[
        { href: "/", label: "Dashboard" },
        { href: "/today", label: "Today" },
        { href: "/performance", label: "Performance" },
        { href: "/ingredients", label: "Ingredients" },
        { href: "/recipes", label: "Recipes" },
        { href: "/weight", label: "Weight" },
        { href: "/profile", label: "Profile" },
      ]}
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <Hero history={history} targets={targets} />

        {!targets ? (
          <div className="mt-12 card-surface px-6 py-6">
            <p className="font-sans text-base">
              Set up your profile so we can compute targets — the Performance
              ledger needs a target line to grade days against.
            </p>
            <a
              href="/profile"
              className="inline-block mt-4 bg-foreground text-background px-5 py-2.5 rounded font-sans text-sm hover:bg-moss transition-colors"
            >
              Profile setup →
            </a>
          </div>
        ) : loading ? (
          <p className="mt-12 font-mono text-xs text-muted-foreground">
            loading…
          </p>
        ) : err ? (
          <p className="mt-12 font-sans text-sm text-persimmon">{err}</p>
        ) : (
          <Ledger history={history!} targets={targets} />
        )}
      </div>
    </Shell>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   HERO — eyebrow + title + three stat blocks
// ════════════════════════════════════════════════════════════════════════════

function Hero({
  history,
  targets,
}: {
  history: MealHistory | null
  targets?: Targets
}) {
  const padded = useMemo(
    () => (history && targets ? padDays(history) : []),
    [history, targets],
  )
  const stats = useMemo(
    () => (targets ? computeStats(padded, targets) : null),
    [padded, targets],
  )

  return (
    <header>
      <p className="eyebrow mb-3 animate-ink-in">
        performance · last {DAYS_BACK} days
      </p>
      <h1 className="font-display text-display tracking-tightest mb-10 animate-fade-up">
        Days you{" "}
        <span className="italic text-moss">met the mark</span>
        <span className="text-muted-foreground">.</span>
      </h1>

      {targets && stats && (
        <section
          className="grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-8 mb-14 animate-fade-up"
          style={{ animationDelay: "120ms" }}
        >
          <StatBlock
            label="ideal days"
            value={`${stats.idealCount}`}
            denom={`/ ${stats.loggedCount} logged`}
            footnote="cal in band · sugar ≤ target · protein & fiber ≥ target"
          />
          <StatBlock
            label="longest streak"
            value={`${stats.longestStreak}`}
            denom={stats.longestStreak === 1 ? "day" : "days"}
            footnote={
              stats.currentStreak > 0
                ? `currently on ${stats.currentStreak}-day streak`
                : "no active streak"
            }
            tone={stats.currentStreak > 0 ? "moss" : undefined}
          />
          <StatBlock
            label="avg daily calories"
            value={`${stats.avgCalories.toFixed(0)}`}
            denom="kcal"
            footnote={`target ${targets.calories.toFixed(0)} kcal`}
          />
        </section>
      )}
    </header>
  )
}

function StatBlock({
  label,
  value,
  denom,
  footnote,
  tone,
}: {
  label: string
  value: string
  denom: string
  footnote: string
  tone?: "moss"
}) {
  const numColor = tone === "moss" ? "text-moss" : "text-foreground"
  return (
    <div>
      <p className="eyebrow mb-2">{label}</p>
      <div className="flex items-baseline gap-2">
        <span
          className={`font-display text-5xl sm:text-6xl tracking-tightest num ${numColor}`}
        >
          {value}
        </span>
        <span className="font-mono text-xs text-muted-foreground">{denom}</span>
      </div>
      <p className="font-mono text-[11px] text-muted-foreground mt-2 leading-relaxed">
        {footnote}
      </p>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   LEDGER — chronological day list
// ════════════════════════════════════════════════════════════════════════════

function Ledger({
  history,
  targets,
}: {
  history: MealHistory
  targets: Targets
}) {
  const padded = useMemo(() => padDays(history), [history])

  if (padded.every((d) => !d.logged)) {
    return (
      <div className="mt-6 card-surface px-6 py-10 text-center">
        <p className="font-display text-2xl tracking-tightest mb-2">
          The ledger is empty.
        </p>
        <p className="font-sans text-sm text-muted-foreground">
          Log a few days of meals and the column will fill in.
        </p>
        <a
          href="/today"
          className="inline-block mt-5 bg-foreground text-background px-5 py-2.5 rounded font-sans text-sm hover:bg-moss transition-colors"
        >
          Log a meal →
        </a>
      </div>
    )
  }

  // Most recent first.
  const ordered = [...padded].reverse()

  return (
    <section className="animate-fade-up" style={{ animationDelay: "200ms" }}>
      <div className="flex items-baseline justify-between mb-5">
        <p className="eyebrow">the ledger</p>
        <p className="font-mono text-[11px] text-muted-foreground tabular-nums">
          {history.start} → {history.end}
        </p>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2 mb-6 font-mono text-[11px] text-muted-foreground">
        <LegendKey color="moss" label="met" />
        <LegendKey color="hollow" label="missed" />
        <LegendKey color="persimmon" label="over limit" />
        <span className="ml-auto italic">
          cal · protein · fiber · sugar
        </span>
      </div>

      <ul className="border-t border-border">
        {ordered.map((d, i) => (
          <DayRow
            key={d.date}
            day={d}
            targets={targets}
            index={i}
          />
        ))}
      </ul>
    </section>
  )
}

function LegendKey({
  color,
  label,
}: {
  color: "moss" | "hollow" | "persimmon"
  label: string
}) {
  const cls =
    color === "moss"
      ? "bg-moss border-moss"
      : color === "persimmon"
      ? "bg-persimmon border-persimmon"
      : "bg-transparent border-border"
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden
        className={`block w-2 h-2 rounded-full border ${cls}`}
      />
      <span>{label}</span>
    </span>
  )
}

type Verdict = "good" | "missed" | "over"

function DayRow({
  day,
  targets,
  index,
}: {
  day: PaddedDay
  targets: Targets
  index: number
}) {
  const verdicts = day.logged
    ? verdictsForDay(day, targets)
    : { calories: null, protein: null, fiber: null, sugar: null }
  const ideal = day.logged && isIdealDay(day, targets)
  const today = day.date === localToday()

  // Left edge tone:
  //   ideal day → moss (a quiet flag of accomplishment)
  //   over calories → persimmon (the only "alert" worth flagging)
  //   otherwise → invisible (hairline only)
  const edgeClass = ideal
    ? "border-moss"
    : verdicts.calories === "over"
    ? "border-persimmon/60"
    : "border-transparent"

  return (
    <li
      className={`group border-b border-border border-l-2 ${edgeClass} pl-4 sm:pl-5 py-5 animate-fade-up`}
      style={{ animationDelay: `${Math.min(index * 40, 800)}ms` }}
    >
      <div className="grid grid-cols-[auto_1fr_auto] items-baseline gap-x-5 gap-y-1">
        {/* Date column */}
        <div className="min-w-[6.5rem]">
          <p className="eyebrow text-foreground">
            {formatDayLabel(day.date)}
            {today && (
              <span className="ml-2 italic text-moss/80 font-mono normal-case tracking-normal">
                today
              </span>
            )}
          </p>
          <p className="font-mono text-[11px] text-muted-foreground tabular-nums mt-1">
            {monthDay(day.date)}
          </p>
        </div>

        {/* Dots */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          <Dot verdict={verdicts.calories} title={`calories · ${describe(day, "calories", targets)}`} />
          <Dot verdict={verdicts.protein} title={`protein · ${describe(day, "protein", targets)}`} />
          <Dot verdict={verdicts.fiber} title={`fiber · ${describe(day, "fiber", targets)}`} />
          <Dot verdict={verdicts.sugar} title={`sugar · ${describe(day, "sugar", targets)}`} />
        </div>

        {/* Verdict line */}
        <p
          className={`font-mono text-[11px] tabular-nums whitespace-nowrap ${
            ideal ? "text-moss" : "text-muted-foreground"
          }`}
        >
          {day.logged ? summary(day, targets, ideal) : "nothing logged"}
        </p>
      </div>

      {/* Numeric tally — second line. Only shown when logged. */}
      {day.logged && (
        <p className="font-mono text-[11px] text-muted-foreground tabular-nums mt-2 pl-[6.5rem] hidden sm:block">
          <span className="text-foreground">{day.calories.toFixed(0)} kcal</span>
          {"  ·  "}
          {day.protein_g.toFixed(0)}g protein
          {"  ·  "}
          {day.fiber_g.toFixed(0)}g fiber
          {"  ·  "}
          {day.carbs_g.toFixed(0)}g carbs
          {"  ·  "}
          {day.sugar_g.toFixed(0)}g sugar
        </p>
      )}
      {day.logged && (
        <p className="font-mono text-[11px] text-muted-foreground tabular-nums mt-2 sm:hidden">
          <span className="text-foreground">{day.calories.toFixed(0)} kcal</span>
          {" · "}
          {day.protein_g.toFixed(0)}p · {day.fiber_g.toFixed(0)}f ·{" "}
          {day.sugar_g.toFixed(0)}s
        </p>
      )}
    </li>
  )
}

function Dot({
  verdict,
  title,
}: {
  verdict: Verdict | null
  title: string
}) {
  // null = empty day; render an extra-muted hollow circle.
  const base = "block w-2.5 h-2.5 rounded-full border transition-colors"
  const cls =
    verdict === "good"
      ? "bg-moss border-moss"
      : verdict === "over"
      ? "bg-persimmon border-persimmon"
      : verdict === "missed"
      ? "bg-transparent border-muted-foreground/50"
      : "bg-transparent border-border" /* not logged */
  return <span aria-hidden className={`${base} ${cls}`} title={title} />
}

// ════════════════════════════════════════════════════════════════════════════
//   Computation
// ════════════════════════════════════════════════════════════════════════════

type PaddedDay = DayTotals & { logged: boolean }

/** Pad the API response so we have one entry per calendar day in the range. */
function padDays(history: MealHistory): PaddedDay[] {
  const map = new Map<string, DayTotals>()
  for (const d of history.days) map.set(d.date, d)
  const start = parseLocalDate(history.start)
  const end = parseLocalDate(history.end)
  const out: PaddedDay[] = []
  for (
    let cursor = new Date(start);
    cursor <= end;
    cursor.setDate(cursor.getDate() + 1)
  ) {
    const ymd = ymdLocal(cursor)
    const hit = map.get(ymd)
    if (hit) {
      out.push({ ...hit, logged: true })
    } else {
      out.push({
        date: ymd,
        calories: 0,
        protein_g: 0,
        fiber_g: 0,
        carbs_g: 0,
        sugar_g: 0,
        logged: false,
      })
    }
  }
  return out
}

function verdictsForDay(d: PaddedDay, t: Targets) {
  return {
    calories: caloriesVerdict(d.calories, t.calories),
    protein: lowerBoundVerdict(d.protein_g, t.protein_g),
    fiber: lowerBoundVerdict(d.fiber_g, t.fiber_g),
    sugar: upperBoundVerdict(d.sugar_g, t.sugar_g),
  }
}

function caloriesVerdict(actual: number, target: number): Verdict {
  // "Ideal" calorie band: [50%, 100%] of target. Below 50% is incomplete
  // logging (graded as "missed"); above target is over the deficit budget.
  if (actual > target) return "over"
  if (actual < target * 0.5) return "missed"
  return "good"
}

function lowerBoundVerdict(actual: number, target: number): Verdict {
  return actual >= target ? "good" : "missed"
}

function upperBoundVerdict(actual: number, target: number): Verdict {
  return actual <= target ? "good" : "over"
}

function isIdealDay(d: PaddedDay, t: Targets): boolean {
  const v = verdictsForDay(d, t)
  return (
    v.calories === "good" &&
    v.protein === "good" &&
    v.fiber === "good" &&
    v.sugar === "good"
  )
}

type Stats = {
  idealCount: number
  loggedCount: number
  longestStreak: number
  currentStreak: number
  avgCalories: number
}

function computeStats(days: PaddedDay[], targets: Targets): Stats {
  let idealCount = 0
  let loggedCount = 0
  let longestStreak = 0
  let runningStreak = 0
  let calSum = 0

  // Walk forward in calendar order. Streaks count consecutive ideal days.
  for (const d of days) {
    if (d.logged) {
      loggedCount++
      calSum += d.calories
      if (isIdealDay(d, targets)) {
        idealCount++
        runningStreak++
        if (runningStreak > longestStreak) longestStreak = runningStreak
      } else {
        runningStreak = 0
      }
    } else {
      // Unlogged days break the streak (charitable interpretation: we
      // can't claim a streak through days we didn't measure).
      runningStreak = 0
    }
  }

  // "Current streak" = trailing consecutive ideal days ending at today
  // (or the most recent ideal day's run, walking from the end backward).
  let currentStreak = 0
  for (let i = days.length - 1; i >= 0; i--) {
    const d = days[i]
    if (d.logged && isIdealDay(d, targets)) currentStreak++
    else break
  }

  return {
    idealCount,
    loggedCount,
    longestStreak,
    currentStreak,
    avgCalories: loggedCount ? calSum / loggedCount : 0,
  }
}

function describe(
  d: PaddedDay,
  macro: "calories" | "protein" | "fiber" | "sugar",
  t: Targets,
): string {
  if (!d.logged) return "not logged"
  const map: Record<typeof macro, [number, number, string]> = {
    calories: [d.calories, t.calories, "kcal"],
    protein: [d.protein_g, t.protein_g, "g"],
    fiber: [d.fiber_g, t.fiber_g, "g"],
    sugar: [d.sugar_g, t.sugar_g, "g"],
  }
  const [a, b, u] = map[macro]
  return `${a.toFixed(0)} / ${b.toFixed(0)} ${u}`
}

function summary(d: PaddedDay, t: Targets, ideal: boolean): string {
  if (ideal) return "ideal day"
  const v = verdictsForDay(d, t)
  if (v.calories === "over") {
    return `over by ${(d.calories - t.calories).toFixed(0)} kcal`
  }
  if (v.sugar === "over") {
    return `sugar +${(d.sugar_g - t.sugar_g).toFixed(0)}g`
  }
  if (v.protein === "missed") {
    return `protein −${(t.protein_g - d.protein_g).toFixed(0)}g`
  }
  if (v.fiber === "missed") {
    return `fiber −${(t.fiber_g - d.fiber_g).toFixed(0)}g`
  }
  if (v.calories === "missed") {
    return "partial log"
  }
  return "on track"
}

function formatDayLabel(ymd: string): string {
  const rel = formatDateRelative(ymd)
  if (rel === "today" || rel === "yesterday") return rel
  const date = parseLocalDate(ymd)
  return date.toLocaleDateString(undefined, { weekday: "short" }).toLowerCase()
}

function monthDay(ymd: string): string {
  const date = parseLocalDate(ymd)
  return date
    .toLocaleDateString(undefined, { month: "short", day: "numeric" })
    .toLowerCase()
}

function parseLocalDate(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number)
  return new Date(y, m - 1, d)
}

function ymdLocal(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}
